import { useApp } from "@hooks/useApp";
import { useCore } from "@hooks/useCore";
import { useKlaviyo } from "@hooks/useKlaviyo";
import { useMounted } from "@hooks/useMounted";
import { useProductContext } from "@hooks/useProduct";
import { useRoutes } from "@hooks/useRoutes";
import { useSettings } from "@hooks/useSettings";
import { useShopify } from "@hooks/useShopify";
import { useWindowSize } from "@hooks/useWindowSize";
import { HistoryLocation } from "@reach/router";
import { type ComponentProps } from "@ts/components";
import {
  Sections,
  type AdditionalContent,
  type SanityKeyed,
  type TemplateProduct,
} from "@ts/sanity";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type LegacyRef,
} from "react";

export type ProductInputProps = ComponentProps & {
  location: HistoryLocation;
  template: TemplateProduct;
  imageGallery?: React.ReactNode;
  howToStyleSection?: React.ReactNode;
  asSeenOnSection?: React.ReactNode;
  featureAndShoeCareSheet?: React.ReactNode;
  productName: string;
  productGender: "Mens" | "Womens";
  productRecommendations?: React.ReactNode;
  productUpsells?: React.ReactNode;
  primaryCollection?: {
    handle: string;
    title: string;
  };
  swatches?: React.ReactNode;
  seoFeatureDescription?: React.ReactNode;
  siblingColours?: string[];
  reviewsSummary?: React.ReactNode;
  reviewNugget?: React.ReactNode;
  children: React.ReactNode;
};

export type ProductOutputProps = {
  additionalContents: SanityKeyed<AdditionalContent>[];
  content: { content: Sections | undefined };
  contentByTag: any;
  description: string;
  flashSaleDescription: string;
  flashSaleTitle: string;
  handle: string;
  isFlashSaleProduct: boolean;
  paths: {
    title: string;
    url?: string;
  }[];
  productGroup?: string;
  sectionsRef: LegacyRef<HTMLDivElement>;
  sku?: string;
  subTitle?: string;
  tags: string[];
  outOfStockMessage: JSX.Element;
  imageGallery: React.ReactNode;
  howToStyleSection?: React.ReactNode;
  asSeenOnSection?: React.ReactNode;
  featureAndShoeCareSheet?: React.ReactNode;
  productName: string;
  productGender: "Mens" | "Womens";
  productRecommendations?: React.ReactNode;
  productUpsells?: React.ReactNode;
  swatches?: React.ReactNode;
  seoFeatureDescription?: React.ReactNode;
  siblingColours?: string[];
  reviewsSummary?: React.ReactNode;
  reviewNugget?: React.ReactNode;
  children: React.ReactNode;
};

export const withProduct =
  (Component: FC<ProductOutputProps>) =>
  ({
    name = "Product",
    template,
    location,
    imageGallery,
    howToStyleSection,
    asSeenOnSection,
    productName,
    productGender,
    productRecommendations,
    productUpsells,
    primaryCollection,
    swatches,
    seoFeatureDescription,
    siblingColours,
    reviewsSummary,
    reviewNugget,
    featureAndShoeCareSheet,
    children,
  }: ProductInputProps) => {
    const { product, loading } = useProductContext();
    const { tags, handle, variants } = product;

    const {
      helpers: { storage, isBrowser },
    } = useCore();
    const {
      config: {
        settings: { keys },
      },
    } = useApp();
    const { routeResolver } = useRoutes();
    const {
      windowSize: { isMD },
    } = useWindowSize();
    const { formatProductGroup } = useShopify();
    const { trackProduct } = useKlaviyo();
    const { hasMounted } = useMounted();
    const { flashSale } = useSettings();
    const addToCartRef = useRef<HTMLDivElement | undefined>();
    const sectionsRef = useRef<HTMLDivElement | undefined>();
    const selectorRef = useRef<{ state: { menuIsOpen: boolean } }>();
    const [isKlaviyoEventDispatched, setIsKlaviyoEventDispatched] =
      useState(false);
    const [displayFooter, setDisplayFooter] = useState(false);
    const [chatButtonNode, setChatButtonNode] = useState(undefined);
    const { additionalFlexibleSections, additionalSections } = template || {};
    const {
      title: flashSaleTitle,
      description: flashSaleDescription,
      enabled: flashSaleEnabled,
      slug: flashSaleSlug,
    } = flashSale;

    const isFlashSaleProduct =
      flashSaleEnabled &&
      tags?.some(
        (tag) => tag?.toLowerCase() === `flash:${flashSaleSlug?.current}`,
      );
    const productGroup = formatProductGroup(tags);
    const subTitle = product?.title?.replace(productName, ``);
    const sku = variants[0]?.sku;

    const filteredContent = additionalSections?.filter((section) =>
      section?.tags?.some((tag) => tags?.includes(tag)),
    );
    const templateContent = { content: additionalFlexibleSections };
    const contentByTag = { content: filteredContent?.[0]?.sections };

    const originalCollection = useMemo(() => {
      const lastCollection = location?.state?.collection;
      if (!lastCollection) return primaryCollection;
      return {
        shopify: {
          shopifyHandle: lastCollection.handle,
        },
        title: lastCollection.title,
      };
    }, [location, primaryCollection]);

    const parentCollection = useMemo(() => {
      if (!location?.state?.collection && !originalCollection) return null;
      return location?.state?.collection
        ? location?.state?.collection
        : originalCollection;
    }, [location, originalCollection]);

    const savedFilters = storage.get(keys.collection_filters);
    const path = parentCollection
      ? routeResolver({ item: parentCollection, type: `collection` })
      : null;
    const paths = useMemo<ProductOutputProps["paths"]>(() => {
      const isGiftCard = product?.tags?.includes("type:giftcard");

      if (isGiftCard) {
        return [];
      }

      return [
        {
          title: productGender === "Womens" ? "Women's" : "Men's",
          url: productGender === "Womens" ? "/women" : "/men",
        },
        {
          title: parentCollection?.title,
          url: `${path}${savedFilters && savedFilters.path === path ? `?${savedFilters.search}` : ""}`,
        },
      ];
    }, [parentCollection, path, savedFilters, product?.tags, productGender]);

    const outOfStockMessage = React.useMemo(() => {
      const templateTextWithCollection =
        template.additionalOutOfStockMessageWithCollection ||
        "Oh no, you've missed out! This is out of stock. Check out our ${collection} for more.";

      const templateTextWithoutCollection =
        template.additionalOutOfStockMessageWithoutCollection ||
        "Oh no, you've missed out! This is out of stock.";

      const _parentCollection = parentCollection
        ? {
            ...parentCollection,
            href: routeResolver({ item: parentCollection, type: `collection` }),
          }
        : null;

      const templateHtml = _parentCollection
        ? `<p>${templateTextWithCollection.replace("${collection}", `<a href="${_parentCollection.href}">${_parentCollection.title}</a>`)}</p>`
        : `<p>${templateTextWithoutCollection}</p>`;

      return <div dangerouslySetInnerHTML={{ __html: templateHtml }} />;
    }, [parentCollection, template]);

    const isInView = (el: HTMLElement) => {
      return el.getBoundingClientRect().top >= 90;
    };

    const trackScrolling = () => {
      const hidden =
        addToCartRef?.current &&
        isInView(addToCartRef.current) &&
        !selectorRef?.current?.state?.menuIsOpen;
      setDisplayFooter((prev) => (prev !== !hidden ? !hidden : prev));
    };

    useEffect(() => {
      if (isMD) {
        return;
      }

      document.addEventListener("scroll", trackScrolling);

      return () => {
        document.removeEventListener("scroll", trackScrolling);
      };
    }, [isMD]);

    useEffect(() => {
      if (hasMounted && !loading && !isKlaviyoEventDispatched) {
        trackProduct(product);
        setIsKlaviyoEventDispatched(true);
      }
    }, [hasMounted, loading, isKlaviyoEventDispatched]);

    useEffect(() => {
      const cssUpdate = (element, style) => {
        for (const property in style) element.style[property] = style[property];
      };

      if (chatButtonNode) {
        cssUpdate(chatButtonNode, {
          marginTop: displayFooter && !isMD ? "-90px" : "-30px",
        });
      }

      return () => {
        if (chatButtonNode) {
          cssUpdate(chatButtonNode, {
            marginTop: "-30px",
          });
        }
      };
    }, [chatButtonNode, displayFooter]);

    useEffect(() => {
      let selfButtonDetect = null;

      const clearButtonDetector = () => {
        if (selfButtonDetect) {
          clearTimeout(selfButtonDetect);
        }
      };

      const getChatButtonElementNode = () => {
        const node =
          document.getElementById("inside_tabs") ||
          document.getElementById("live-chat-container");
        if (node) {
          setChatButtonNode(node);
          clearButtonDetector();
        } else {
          selfButtonDetect = setTimeout(getChatButtonElementNode, 3000);
        }
      };

      if (isBrowser) {
        getChatButtonElementNode();
      }

      return () => {
        clearButtonDetector();
      };
    }, [isBrowser]);

    Component.displayName = name;
    return (
      <Component
        content={templateContent}
        contentByTag={contentByTag}
        flashSaleDescription={flashSaleDescription}
        flashSaleTitle={flashSaleTitle}
        handle={handle}
        isFlashSaleProduct={isFlashSaleProduct}
        paths={paths}
        productName={productName}
        productGender={productGender}
        productGroup={productGroup}
        sectionsRef={sectionsRef}
        sku={sku}
        subTitle={subTitle}
        outOfStockMessage={outOfStockMessage}
        imageGallery={imageGallery}
        howToStyleSection={howToStyleSection}
        asSeenOnSection={asSeenOnSection}
        productRecommendations={productRecommendations}
        productUpsells={productUpsells}
        swatches={swatches}
        seoFeatureDescription={seoFeatureDescription}
        siblingColours={siblingColours}
        reviewsSummary={reviewsSummary}
        reviewNugget={reviewNugget}
        featureAndShoeCareSheet={featureAndShoeCareSheet}
      >
        {children}
      </Component>
    );
  };
